'use client';
import './accounts.scss';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Link, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';

/* import enums & context */
import { useShoppingCart } from '@/context/ShoppingCartContext';

import CreateAccount from '../auth/createAccount';
import Signin from '../auth/signin';
import customToaster from '../toast/CustomToaster';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEvent } from '@/services/eventEnums';
import { identifyZohoVisitor, resetZohoVisitor } from '@/services/zohoSalesIQ';
import { logoutChannel, splitFullName } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { getProfile_rh } from '@/network/routeHandlerService/orderNew';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import { getIntercomToken } from '@/network/serverApis/intercom';

import posthog from 'posthog-js';
import useIntercomScript from '@/hooks/intercomScript';

import { checkUserSession } from '@/network/serverApis/login';

interface Profile {
  id: string;
  name: string;
  email: string;
  [key: string]: any; // For any additional profile properties
}

export default function Accountsdrawer({
  secrets,
}: {
  secrets: { intercomAppId: string | undefined };
}) {
  const { resetCart, drawer, setDrawer } = useShoppingCart();
  const { initSession, userData, setAccountDrawerRef } = useAuthenticationContext();
  const router = useRouter();
  const pathname = usePathname();
  // const { user, error } = useUser();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { t } = useTranslation('drawer');
  const intercomScript = useIntercomScript({ appId: secrets.intercomAppId || '' });

  const waitForPostHogReady = () =>
    new Promise<string>(resolve => {
      const tryGet = () => {
        const id = posthog.get_distinct_id();
        const hasLoaded = posthog.__loaded && posthog.__loaded === true;
        if (id && hasLoaded) {
          resolve(id);
        } else {
          setTimeout(tryGet, 50);
        }
      };
      tryGet();
    });

  const bootVisitor = async () => {
    if (typeof window === 'undefined') return;
    if ((window as any).__intercomBooted) return;

    (window as any).Intercom('boot', {
      app_id: secrets.intercomAppId,
      api_base: 'https://api-iam.intercom.io',
      // if adding user_id please add get the intercom token as in logged in user flow and add it here intercom_user_jwt: intercomToken,
      // user_id: distinctId,
      // this is for testing purposes in dev only
      // hide_default_launcher: false,
    });

    // retreive the old user messages
    // (window as any).Intercom('update', {
    //   user_id: distinctId,
    //   enable_product_tour: posthog.getFeatureFlag('product_tour_ff') === 'test' ? true : false,
    //   // enable_tour:true

    //   // custom_attributes: {
    //   //   enable_product_tour: posthog.getFeatureFlag('product_tour_ff') === 'test' ? true : false,
    //   // },
    // });

    (window as any).__intercomBooted = true;
  };

  useEffect(() => {
    // setting state to use in other places
    setAccountDrawerRef.current = setIsOpen as (open: boolean) => void;
    const getToken = async (): Promise<void> => {
      if (!secrets.intercomAppId) console.error('Intercom app ID is not provided');
      // eslint-disable-next-line require-use-server
      // const token = await accessToken();

      await initSession();
      if (typeof window !== 'undefined' && window.localStorage) {
        // eslint-disable-next-line require-use-server
        const isuserLoggedIn = await checkUserSession();

        if (isuserLoggedIn?.user) {
          // localStorage.setItem('ecToken', token);
          const profile = (await getProfile_rh()) as Profile | undefined;

          MixpanelTracking.getInstance().identify(profile?.id || '', profile?.email);
          // covers Auth0 hosted sign-up and returning logins; uses the same backend
          // user id as createAccount so Zoho links one visitor per person
          if (profile) {
            identifyZohoVisitor({
              id: profile.id,
              email: profile.email,
              ...splitFullName(profile.name),
            });
          }
          // eslint-disable-next-line require-use-server
          const intercomToken = await getIntercomToken({
            name: profile?.name,
            email: profile?.email,
            user_id: profile?.id,
          });
          if (typeof profile === 'undefined') {
            return customToaster(t('common:generalError'));
          }

          const profileData = JSON.stringify(profile);
          localStorage.setItem('profile', profileData);

          //intercom for logged in users
          (window as any).Intercom('boot', {
            app_id: secrets.intercomAppId,
            api_base: 'https://api-iam.intercom.io',
            intercom_user_jwt: intercomToken,
            user_id: profile?.id,
            name: profile?.name,
            email: profile?.email,
            // this is for testing purposes in dev only
            // hide_default_launcher: false,
          });

          // retreive the old user messages
          // (window as any).Intercom('update', {
          //   name: profile?.name,
          //   email: profile?.email,
          //   user_id: profile?.id,
          //   enable_aerial_message:
          //     posthog.getFeatureFlag('enable_aerial_message') === 'test' ? true : false,
          //   // custom_attributes:{
          //   // }
          //   // custom_attributes: {
          //   //   enable_aerial_message:
          //   //     posthog.getFeatureFlag('enable_aerial_message') === 'test' &&
          //   //     process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.EXPLORE
          //   //       ? true
          //   //       : false,
          //   // },
          //   // enable_aerial_message:
          //   //   posthog.getFeatureFlag('enable_aerial_message') === 'test' &&
          //   //   process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.EXPLORE
          //   //     ? true
          //   //     : false,
          // });
        } else {
          bootVisitor();
          localStorage.removeItem('ecToken');
        }
      }
    };

    if (pathname !== '/verify-email') {
      getToken();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (drawer) {
      setIsOpen(drawer);
      setDrawer(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer]);

  const handleLogout = (): void => {
    resetCart();
    MixpanelTracking.getInstance().track(MixpanelEvent.ACCOUNT_LOG_OUT);
    (window as any).Intercom('shutdown');
    resetZohoVisitor();
    // this only works for multi tabs
    logoutChannel.postMessage('logout');
    router.push('/auth/logout');
  };

  const toggleDrawer =
    (open: boolean) =>
    (event: React.KeyboardEvent | React.MouseEvent): void => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setIsOpen(open);
    };

  const list = (): React.ReactElement => (
    <>
      <Box className="drawer-header">
        <Typography variant="h5">{t('accountTitle')}</Typography>
        <IconButton
          size="large"
          onClick={toggleDrawer(false)}
          name="Close Account"
          aria-label="close-account"
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {userData ? (
        <Box className="drawer-content">
          <Stack direction="column" spacing={2} className="account-details">
            <Typography variant="h5">{userData.name}</Typography>
            <Link
              onClick={() => {
                setIsOpen(false);
                router.push('/my-account');
              }}
            >
              {t('myAccount')}
            </Link>
          </Stack>
        </Box>
      ) : (
        <Box className="drawer-content">
          <Typography variant="body2">{t('createAccount1')}</Typography>
          <CreateAccount t={t} setAccountDrawer={setIsOpen} />
          <Signin t={t} />
        </Box>
      )}
      <Box className="drawer-footer">
        <List className="footer-list">
          {[
            { href: process.env.NEXT_PUBLIC_TERMS_OF_SERVICES, text: 'accountLink1' },
            { href: process.env.NEXT_PUBLIC_EUDLA, text: 'accountLink2' },
            { href: process.env.NEXT_PUBLIC_PRIVACY_POLICY, text: 'accountLink3' },
            { href: process.env.NEXT_PUBLIC_THIRD_PARTY_LICENSES, text: 'accountLink4' },
          ].map(link => (
            <ListItem key={link.text}>
              <Link href={link.href} target="_blank" aria-label={`${t(link?.text)}-btn`}>
                {t(link.text)}
              </Link>
            </ListItem>
          ))}

          {userData && (
            <ListItem>
              <Button
                name="Log Out"
                size="large"
                variant="contained"
                onClick={handleLogout}
                fullWidth
              >
                {t('logOut')}
              </Button>
            </ListItem>
          )}
        </List>
      </Box>
    </>
  );

  return (
    <React.Fragment>
      <IconButton onClick={toggleDrawer(true)} aria-label="Account-Drawer-Toggle">
        <AccountCircleOutlinedIcon />
      </IconButton>
      <Drawer
        ModalProps={{
          className: 'skywatch-drawer-wrapper',
        }}
        PaperProps={{ className: 'skywatch-drawer' }}
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
      >
        {list()}
      </Drawer>
    </React.Fragment>
  );
}
